import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MessengerPlatform, UserProfile } from "@carry/messenger-platform";
import { formatDisplayDate, generatePublicId } from "@carry/shared";
import type { StoredDriverSession } from "./auth";
import { DateStrip } from "./components/DateStrip";
import { Header } from "./components/Header";
import { MissingOdometerDialog } from "./components/MissingOdometerDialog";
import {
  OdometerDialog,
  type ClockInAlcohol,
  type ClockInWeather,
} from "./components/OdometerDialog";
import {
  clockInDriver,
  clockOutDriver,
  confirmInstruction,
  createDriverDelayReport,
  editDriverStopTimestamps,
  getDriverTodayDailyReport,
  revertDriverStopStatus,
  reportLocationDriver,
  shouldQueueDriverError,
  updateDriverStopStatus,
  useDriverAttendance,
  useDriverSchedule,
  type ClockInInput,
  type ClockOutInput,
  type CreateDriverDelayReportInput,
  type DriverFetcher,
  type DriverExpense,
  type EditDriverStopTimestampsInput,
} from "./hooks";
import { captureCurrentPosition } from "./lib/geolocation";
import { startLocationReporting } from "./lib/location-reporter";
import {
  emptyRouteView,
  clampRouteDate,
  departureBlockedReason,
  evaluateClockInGate,
  formatRouteDate,
  hasEditingRoute,
  proofBlockedMessage,
  initialScreenFromUrl,
  jstDateOffset,
  routeDateRange,
  routesToView,
  stopsMissingArrivalOdometer,
  type ExecutionBlockedReason,
  type Screen,
  type Tab,
} from "./lib/normalize";
import {
  discardDeadLetter,
  enqueueAction,
  flushOfflineQueue,
  queueSize,
  readDeadLetters,
  retryDeadLetter,
  type DeadLetterEntry,
} from "./offline-queue";
import { AlcoholScreen } from "./screens/AlcoholScreen";
import { ApiStatusBanner } from "./screens/ApiStatusBanner";
import { DailyReportScreen } from "./screens/DailyReportScreen";
import { DelayReport } from "./screens/DelayReport";
import { ExpenseFormScreen } from "./screens/ExpenseFormScreen";
import { ExpensesListScreen } from "./screens/ExpensesListScreen";
import { Footer } from "./screens/Footer";
import { HistoryScreen } from "./screens/HistoryScreen";
import { InspectionScreen } from "./screens/InspectionScreen";
import { OrderDocumentScreen } from "./screens/OrderDocumentScreen";
import { QuickActions } from "./screens/QuickActions";
import { RefuelingScreen } from "./screens/RefuelingScreen";
import { ProofUpload } from "./screens/ProofUpload";
import { StatusRevertConfirm } from "./screens/StatusRevertConfirm";
import { StopDetail } from "./screens/StopDetail";
import { TimestampEdit } from "./screens/TimestampEdit";
import { StopGroups } from "./screens/StopGroups";
import { SummaryStrip } from "./screens/SummaryStrip";

export { OdometerDialog } from "./components/OdometerDialog";

// ────────────────────────────────────────────────────────
// DriverApp — ドライバー画面 (API 接続版)
// schedule.routes を正規化した activeStops を表示する。データが無ければ空表示。
// ────────────────────────────────────────────────────────

interface DriverAppProps {
  platform: MessengerPlatform;
  apiBaseUrl: string;
  session?: StoredDriverSession | null;
}

type ProfileState =
  | { status: "loading" }
  | { status: "ready"; profile: UserProfile }
  | { status: "error"; message: string };

/**
 * dead-letter を localStorage から読み直す state updater。
 * readDeadLetters() は内容が同じでも毎回新しい配列を返すため、そのまま setState すると
 * 必ず再レンダリングが起きる。これが effect の再実行と噛み合うと無限ループになるので、
 * 内容が変わらない限り前回の参照を保持して再レンダリングを止める。
 */
function syncDeadLetters(prev: DeadLetterEntry[]): DeadLetterEntry[] {
  const next = readDeadLetters();
  const unchanged =
    prev.length === next.length && prev.every((entry, i) => entry.action.id === next[i]?.action.id);
  return unchanged ? prev : next;
}

// ───── ルート ─────

export function DriverApp({ platform, apiBaseUrl, session = null }: DriverAppProps) {
  const [state, setState] = useState<ProfileState>({ status: "loading" });
  // #768: 表示中の配送日。undefined = 今日 (API 既定に委ねる)。
  // 日付文字列で固定しないので、アプリを開いたまま日付を跨いでも「今日」がずれない。
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  // 起動時に LINE Push deepLink (?screen=...&stop=...) を読み初期画面を決める。
  // detail で指定された stop が当日のルートに存在しない場合は selectedStop が null になり
  // 自然に list 相当の表示に戻る (下記 selectedStop / レンダリング条件)。
  const [screen, setScreen] = useState<Screen>(() =>
    typeof window === "undefined" ? { kind: "list" } : initialScreenFromUrl(window.location.search),
  );
  const [clockInDialog, setClockInDialog] = useState<{ open: boolean }>({ open: false });
  const [clockOutDialog, setClockOutDialog] = useState<{ open: boolean }>({ open: false });
  // #628: 退勤操作時に到着オドメーター未入力を提示する画面。退勤自体はブロックしない。
  const [missingOdoDialog, setMissingOdoDialog] = useState<{ open: boolean }>({ open: false });
  const [actionError, setActionError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  // disabled の再レンダーより先に同期的に立て、固定表示と便別表示の同一 stop や連打からの二重実行を防ぐ。
  const actionInFlightRef = useRef(false);
  const [pendingQueue, setPendingQueue] = useState(() => queueSize());
  const [editingExpense, setEditingExpense] = useState<DriverExpense | null>(null);
  const [expenseMutationNotice, setExpenseMutationNotice] = useState<string | null>(null);
  const [driverRecordRevision, setDriverRecordRevision] = useState(0);
  // Issue #252: flush が破棄したアクションの退避先 (dead-letter)。バナーで通知し再試行/破棄できる。
  const [deadLetters, setDeadLetters] = useState<DeadLetterEntry[]>(() => readDeadLetters());
  // #768: 表示日の解決 (レンダー内算出。useEffect 不使用)。60 秒ポーリングの度に再レンダーされるため、
  // 日付を跨いでも「今日」の判定が自動で追従する。
  const viewDate = selectedDate ?? jstDateOffset(0);
  /** 今日を見ているか。今日以外 (過去日・翌日) はすべて閲覧のみ。 */
  const isTodayView = viewDate === jstDateOffset(0);
  const dateRange = routeDateRange();
  // 過去日はどのタブにも属さない。タブ自体は「今日」へ戻る導線として押せるまま残す。
  const activeTab: Tab | undefined = isTodayView
    ? "今日"
    : viewDate === jstDateOffset(1)
      ? "明日"
      : undefined;
  /** 表示日を切り替える。今日は undefined へ正規化し、閲覧できる範囲外は上下限へ丸める。 */
  const showRouteDate = useCallback((date: string) => {
    const next = clampRouteDate(date);
    setSelectedDate(next === jstDateOffset(0) ? undefined : next);
  }, []);
  const schedule = useDriverSchedule({
    apiBaseUrl,
    session,
    pollMs: 60_000,
    deliveryDate: selectedDate,
  });
  const attendance = useDriverAttendance({ apiBaseUrl, session, pollMs: 60_000 });
  // #523: 確定 → 出勤後に配車担当が確定解除すると、レーンは編集中 (assigned) で返ってくる。
  // 編集が終わるまで打刻させず、案内バナーだけ出す (レアケースなので簡潔に止めるだけ)。
  // 今日以外 (過去日・翌日) は閲覧のみ。他日のレーンの編集で当日の再送まで止めない。
  const routeEditing = isTodayView && hasEditingRoute(schedule.routes);
  // 配送実行を止めている理由。案内文を出し分けるため理由まで持つ。
  const executionBlockedReason: ExecutionBlockedReason | null = routeEditing
    ? "route-editing"
    : !session || attendance.deliverySafety.allowed
      ? null
      : "alcohol";
  const deliveryExecutionAllowed = executionBlockedReason === null;
  const fetcher: DriverFetcher = useMemo(() => ({ apiBaseUrl, session }), [apiBaseUrl, session]);
  const refreshDriverRecordQueries = useCallback(async () => {
    // 変更自体は成功済みなので、再取得失敗で成功表示や画面遷移を巻き戻さない。
    // revision も進め、次に日報画面を開いた時は必ず最新データを取得する。
    await getDriverTodayDailyReport(fetcher).catch(() => undefined);
    setDriverRecordRevision((revision) => revision + 1);
  }, [fetcher]);

  // Issue #338/#548: 確定レーンを持たないドライバーは出勤打刻できない (アプリ側の制御)。
  // 一覧の出勤ボタンと勤怠 deep link (LINE リッチメニューの出退勤) の両方がこの判定を通る。
  // 退勤は出勤済みなら常に可能にする — ルートが後から外れても締め出さないため。
  const clockInGate = useMemo(
    () => evaluateClockInGate({ routes: schedule.routes, error: schedule.error }),
    [schedule.routes, schedule.error],
  );
  const clockInBlockedReason = clockInGate.status === "blocked" ? clockInGate.reason : null;
  // 当日ルートの取得が (成功・失敗いずれかで) 決着したか。決着前は未入力オドメーターを判定できない。
  const scheduleResolved = schedule.routes !== null || schedule.error !== null;

  // 勤怠 deep link は状態取得後に既存の確認ダイアログを開くだけとし、
  // URL を開いた時点では出退勤 API を実行しない。状態不明時は安全側で一覧へ戻す。
  useEffect(() => {
    if (screen.kind !== "attendance" || !attendance.loaded) return;
    if (!session || attendance.error) {
      setSelectedDate(undefined);
      setScreen({ kind: "list" });
      return;
    }
    if (!attendance.attendance?.clock_in_at) {
      // #548: 出勤ゲートが閉じている / まだ判定できないうちはダイアログを開かない。
      // blocked のときは勤怠画面に理由を出したまま留まり、ルートが割り当てられれば
      // ゲートが allowed に変わってこの effect が再実行され、そのまま打刻できる。
      if (clockInGate.status !== "allowed") return;
      setClockInDialog({ open: true });
      return;
    }
    if (!attendance.attendance.clock_out_at) {
      // #628: 当日ルートが確定する前に退勤確認を開くと、未入力オドメーターの提示を取りこぼす。
      // 取得完了 (成功/失敗どちらでも) まで待ってから退勤フローに入る。
      if (!scheduleResolved) return;
      openClockOutFlow();
      return;
    }
    setSelectedDate(undefined);
    setScreen({ kind: "list" });
  }, [
    attendance.attendance?.clock_in_at,
    attendance.attendance?.clock_out_at,
    attendance.error,
    attendance.loaded,
    clockInGate.status,
    scheduleResolved,
    screen.kind,
    session,
  ]);

  // F10 配車リアルタイム: 出勤中 (clock_in 済 & 未退勤) のみ GPS 現在地を報告する。
  // watchPosition を購読する副作用なので useEffect が妥当。送信間引き (前回送信から 500m 超)
  // は location-reporter の距離ゲートが行う。best-effort (失敗は握り潰す)。
  const isOnDuty = !!attendance.attendance?.clock_in_at && !attendance.attendance?.clock_out_at;
  useEffect(() => {
    if (!isOnDuty) return;
    const { stop } = startLocationReporting({
      send: (input) => reportLocationDriver(fetcher, input),
      thresholdM: 500,
    });
    return stop;
  }, [isOnDuty, fetcher]);

  // ハードウェア戻る (LINE WebView, ブラウザ): list 以外の screen で
  // history を 1 段積み、popstate で list に戻す
  useEffect(() => {
    if (screen.kind === "list") return;
    if (typeof window === "undefined") return;
    window.history.pushState({ cpScreen: screen.kind }, "");
    const onPop = () => {
      if (screen.kind === "attendance") {
        setSelectedDate(undefined);
        setClockInDialog({ open: false });
        setClockOutDialog({ open: false });
        setMissingOdoDialog({ open: false });
      }
      setScreen({ kind: "list" });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [screen.kind]);

  const [flushedAt, setFlushedAt] = useState<number | null>(null);
  // schedule / attendance は hook が毎レンダー新しいオブジェクトを返すため、これらを
  // そのまま依存に置くと flushQueue の identity が毎レンダー変わり、下の
  // useEffect([flushQueue]) が毎レンダー再実行されてしまう。実際に使うのは refetch
  // (useCallback で安定) と loaded だけなので、その安定した参照だけに依存する。
  const scheduleRefetch = schedule.refetch;
  const attendanceRefetch = attendance.refetch;
  const attendanceLoaded = attendance.loaded;
  // オフラインキュー flush (session 確立時 & online 復帰時)
  const flushQueue = useCallback(async () => {
    if (!session || !attendanceLoaded) return;
    const result = await flushOfflineQueue({
      apiBaseUrl,
      deliveryExecutionAllowed,
    });
    setPendingQueue(queueSize());
    // 破棄が発生していれば dead-letter を読み直し、バナーの失敗通知に反映する (Issue #252)
    setDeadLetters(syncDeadLetters);
    if (result.processed > 0) {
      setFlushedAt(Date.now());
      void scheduleRefetch();
      void attendanceRefetch();
    }
  }, [
    apiBaseUrl,
    session,
    scheduleRefetch,
    attendanceRefetch,
    attendanceLoaded,
    deliveryExecutionAllowed,
  ]);

  // dead-letter からメインキューへ戻して即時再送する。body は初回操作時のまま
  // (#236 の idempotency_key / occurred_at を含む) なので二重登録にならない。
  const handleRetryDeadLetter = useCallback(
    (actionId: string) => {
      if (!retryDeadLetter(actionId)) return;
      setDeadLetters(syncDeadLetters);
      setPendingQueue(queueSize());
      void flushQueue();
    },
    [flushQueue],
  );

  const handleDiscardDeadLetter = useCallback((actionId: string) => {
    discardDeadLetter(actionId);
    setDeadLetters(syncDeadLetters);
  }, []);

  useEffect(() => {
    void flushQueue();
    if (typeof window === "undefined") return;
    const handler = () => void flushQueue();
    window.addEventListener("online", handler);
    return () => window.removeEventListener("online", handler);
  }, [flushQueue]);

  useEffect(() => {
    let cancelled = false;
    platform
      .getProfile()
      .then((profile) => {
        if (!cancelled) setState({ status: "ready", profile });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [platform]);

  // F04 API-38: 配送指示詳細を開いた時点で当該ルートの指示を確認済みとして記録する
  // (一括自動既読化を廃止し、詳細表示時 fire-and-forget・冪等に変更)。
  const confirmRouteInstruction = useCallback(
    (routeId: string | undefined) => {
      if (!session || !routeId) return;
      void confirmInstruction(fetcher, routeId);
    },
    [fetcher, session],
  );

  // Issue #611: 地図リンクは LIFF 内ブラウザから出る必要があるため platform 経由で開く
  const openExternalUrl = useCallback((url: string) => platform.openExternalUrl(url), [platform]);

  const displayName = state.status === "ready" ? state.profile.displayName : "";
  const routeView = useMemo(
    () => routesToView(schedule.routes, schedule.timeSlotBoundaries) ?? emptyRouteView(),
    [schedule.routes, schedule.timeSlotBoundaries],
  );
  const assignedVehiclePublicIds = useMemo(() => {
    const vehicleIds =
      schedule.routes
        ?.map((route) => route.vehicle_id)
        .filter((vehicleId): vehicleId is string => !!vehicleId) ?? [];
    return Array.from(new Set(vehicleIds));
  }, [schedule.routes]);

  // #628: 退勤時に本人へ提示する到着オドメーター未入力の立寄り先。
  // 今日以外を見ているときは routeView が他日のルートになるため対象外 (更新系操作も出していない)。
  // 配送実行が止まっている間はオドメーター更新自体を止めているので提示もしない。
  const missingOdometerStops = useMemo(
    () =>
      !isTodayView || !deliveryExecutionAllowed ? [] : stopsMissingArrivalOdometer(routeView.stops),
    [isTodayView, deliveryExecutionAllowed, routeView.stops],
  );

  const selectedStop = useMemo(() => {
    if (
      screen.kind !== "detail" &&
      screen.kind !== "status" &&
      screen.kind !== "delay" &&
      screen.kind !== "revert" &&
      screen.kind !== "timestamp-edit" &&
      screen.kind !== "proof"
    )
      return null;
    return routeView.stops.find((s) => s.id === screen.stopId) ?? null;
  }, [screen, routeView]);

  /**
   * #616: 配送先詳細から経費フォームを開いたときの固定紐付け先。
   * 対象 stop が当日のルートから消えた場合は undefined になり、通常の (紐付け任意の) 入力に戻る。
   */
  const expenseLockedStop = useMemo(() => {
    if (screen.kind !== "expense" || !screen.stopId) return undefined;
    const stop = routeView.stops.find((s) => s.id === screen.stopId);
    if (!stop) return undefined;
    return {
      routeStopPublicId: stop.id,
      deliveryDate: viewDate,
      name: stop.deliverySpotCompany ?? stop.name,
    };
  }, [screen, routeView, viewDate]);

  async function runAction(fn: () => Promise<void>) {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setActing(true);
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      actionInFlightRef.current = false;
      setActing(false);
    }
  }

  function enqueueRetryable(error: unknown, action: Parameters<typeof enqueueAction>[0]) {
    if (!shouldQueueDriverError(error)) return;
    enqueueAction(action);
    setPendingQueue(queueSize());
  }

  // 出発打刻 (stop.status = 'in_transit')
  // pending の stop で「出発打刻」を押すと in_transit に進め、到着打刻ボタンへ切り替わる。
  // API の遷移ルール上 pending → arrived は不可なため、この中間打刻が必須。
  // actual_departure は「その stop を離れた時刻」を表し完了打刻で記録するため、ここでは送らない。
  async function departStop(stopId: string) {
    await runAction(async () => {
      const pos = await captureCurrentPosition();
      const geo = pos ? { latitude: pos.latitude, longitude: pos.longitude } : {};
      const body = { status: "in_transit" as const, ...geo };
      try {
        await updateDriverStopStatus(fetcher, stopId, body);
      } catch (e) {
        enqueueRetryable(e, {
          kind: "update-stop-status",
          path: `/api/v1/driver/stops/${stopId}/status`,
          method: "PUT",
          body,
        });
        throw e;
      }
      await schedule.refetch();
    });
  }

  // 到着打刻 (stop.status = 'arrived')
  // F29 委託先支払距離: 配達先では到着時オドメーター (km) を任意で受け取り body に添付する (空欄なら未送信)。
  async function arriveStop(stopId: string, arrivalOdometer?: number) {
    await runAction(async () => {
      const iso = new Date().toISOString();
      // F10 監査連携: ステータス変更時の GPS を best-effort で添付 (失敗時は座標なしで続行)。
      const pos = await captureCurrentPosition();
      const geo = pos ? { latitude: pos.latitude, longitude: pos.longitude } : {};
      const body = {
        status: "arrived" as const,
        actual_arrival: iso,
        ...geo,
        ...(arrivalOdometer !== undefined ? { arrival_odometer: arrivalOdometer } : {}),
      };
      try {
        await updateDriverStopStatus(fetcher, stopId, body);
      } catch (e) {
        enqueueRetryable(e, {
          kind: "update-stop-status",
          path: `/api/v1/driver/stops/${stopId}/status`,
          method: "PUT",
          body,
        });
        throw e;
      }
      await schedule.refetch();
    });
  }

  // F29 委託先支払距離: 到着済み/完了済みの配達先に後からオドメーターを入力・修正する。
  // status / actual_arrival は送らず arrival_odometer のみ更新 (打刻状態は変えない)。空欄は null でクリア。
  async function editStopOdometer(stopId: string, arrivalOdometer?: number) {
    const body = { arrival_odometer: arrivalOdometer ?? null };
    await runAction(async () => {
      try {
        await updateDriverStopStatus(fetcher, stopId, body);
      } catch (e) {
        enqueueRetryable(e, {
          kind: "update-stop-status",
          path: `/api/v1/driver/stops/${stopId}/status`,
          method: "PUT",
          body,
        });
        throw e;
      }
      await schedule.refetch();
    });
  }

  // 完了打刻 (stop.status = 'completed')
  async function completeStop(stopId: string) {
    await runAction(async () => {
      const iso = new Date().toISOString();
      const pos = await captureCurrentPosition();
      const geo = pos ? { latitude: pos.latitude, longitude: pos.longitude } : {};
      const body = { status: "completed" as const, actual_departure: iso, ...geo };
      try {
        await updateDriverStopStatus(fetcher, stopId, body);
      } catch (e) {
        enqueueRetryable(e, {
          kind: "update-stop-status",
          path: `/api/v1/driver/stops/${stopId}/status`,
          method: "PUT",
          body,
        });
        throw e;
      }
      await schedule.refetch();
    });
  }

  // H-7: 業務上の取消。配送ステータス履歴に理由を残し、完了済みグループへ移す。
  async function cancelStop(stopId: string, reason: string) {
    const iso = new Date().toISOString();
    const pos = await captureCurrentPosition();
    const geo = pos ? { latitude: pos.latitude, longitude: pos.longitude } : {};
    const body = { status: "cancelled" as const, actual_departure: iso, comment: reason, ...geo };
    await runAction(async () => {
      try {
        await updateDriverStopStatus(fetcher, stopId, body);
      } catch (e) {
        enqueueRetryable(e, {
          kind: "update-stop-status",
          path: `/api/v1/driver/stops/${stopId}/status`,
          method: "PUT",
          body,
        });
        throw e;
      }
      await schedule.refetch();
    });
  }

  // F14 Slice 3: ステータス巻き戻し. completed → in_transit に戻し
  // delivery_status_audit に rollback として記録する。
  async function revertStop(stopId: string, reason: string | undefined) {
    await runAction(async () => {
      try {
        await revertDriverStopStatus(fetcher, stopId, reason);
      } catch (e) {
        enqueueRetryable(e, {
          kind: "revert-stop-status",
          path: `/api/v1/driver/stops/${stopId}/status/revert`,
          method: "POST",
          body: reason !== undefined ? { reason } : {},
        });
        throw e;
      }
      await schedule.refetch();
    });
  }

  // F14 Slice 3: 打刻修正. 当日内 (JST) の actual_departure を上書きし
  // delivery_status_audit に timestamp_edit として記録する。
  async function editTimestamps(stopId: string, input: EditDriverStopTimestampsInput) {
    await runAction(async () => {
      try {
        await editDriverStopTimestamps(fetcher, stopId, input);
      } catch (e) {
        enqueueRetryable(e, {
          kind: "timestamp-edit",
          path: `/api/v1/driver/stops/${stopId}/status-timestamps`,
          method: "PUT",
          body: input as unknown as Record<string, unknown>,
        });
        throw e;
      }
      await schedule.refetch();
    });
  }

  // F14 Slice 2: 遅延報告. delivery_delay_reports に保存し
  // admin/staff へ LINE Push + in_app 通知が配信される。
  async function reportDelay(stopId: string, input: CreateDriverDelayReportInput) {
    await runAction(async () => {
      const body: CreateDriverDelayReportInput & { request_id: string } = {
        ...input,
        request_id: generatePublicId(),
      };
      try {
        await createDriverDelayReport(fetcher, stopId, body);
      } catch (e) {
        enqueueRetryable(e, {
          kind: "delay-report",
          path: `/api/v1/driver/stops/${stopId}/delay-reports`,
          method: "POST",
          body,
        });
        throw e;
      }
      await schedule.refetch();
    });
  }

  // 日報画面のバッジ用: このセッションで点検・アルコールを完了したか追跡する
  const [inspectionDone, setInspectionDone] = useState(false);
  const [alcoholDone, setAlcoholDone] = useState(false);

  // #401: 点検・給油・経費は日報にぶら下がり、日報には出勤が必要 (出勤にはルート割当が必要 #338)。
  // 出勤前は作業導線を閉じる。点検は退勤ゲート (サーバ) が退勤前実施を担保するため退勤後も閉じる。
  // session が無いスタブ表示ではゲートしない (deliveryExecutionAllowed と同じ規則)。
  const clockInAt = attendance.attendance?.clock_in_at ?? null;
  const clockOutAt = attendance.attendance?.clock_out_at ?? null;
  const workGateReason =
    session && !clockInAt ? "車両点検・給油・経費の追加は出勤後に利用できます" : null;
  const inspectionGateReason =
    workGateReason ??
    (session && clockInAt && clockOutAt ? "退勤済みのため車両点検は利用できません" : null);

  // #401: 出発打刻時の点検未実施警告。サーバ truth (attendance.inspectionDone) と
  // このセッションでの実施 (inspectionDone) のどちらかが立っていれば実施済みとみなす。
  const inspectionRecorded = inspectionDone || attendance.inspectionDone;
  const [departInspectionWarn, setDepartInspectionWarn] = useState<{
    open: boolean;
    stopId: string | null;
  }>({ open: false, stopId: null });

  /** 出発打刻の入口。点検未実施なら警告を挟む (「このまま出発」で続行可、最終担保は退勤ゲート)。 */
  function requestDepart(stopId: string) {
    if (session && !inspectionRecorded) {
      setDepartInspectionWarn({ open: true, stopId });
      return;
    }
    void departStop(stopId);
  }

  // F29 委託先支払距離: 配達先のオドメーター入力ダイアログ。
  // mode='arrive' は到着打刻と同時入力、mode='edit' は到着済み/完了済みの後追い入力・修正。
  const [arriveOdoDialog, setArriveOdoDialog] = useState<{
    open: boolean;
    stopId: string | null;
    mode: "arrive" | "edit";
  }>({
    open: false,
    stopId: null,
    mode: "arrive",
  });
  // F16/F20: 位置情報取得が失敗/拒否されたときの警告 (打刻自体は記録される)。
  const [geoWarning, setGeoWarning] = useState<string | null>(null);

  async function performClockIn(
    odometer_start: number | undefined,
    alcohol: ClockInAlcohol,
    weather: ClockInWeather,
  ) {
    // #548: 出勤ゲートは「ボタンを出すか」だけでなく送信直前にも効かせる。表示条件だけで守ると、
    // ダイアログを開いた後に確定解除された場合や、導線が増えたときにまた素通りする。
    if (clockInGate.status !== "allowed") {
      setClockInDialog({ open: false });
      setActionError(
        clockInGate.status === "blocked"
          ? clockInGate.reason
          : "本日のルートを確認中です。しばらく待ってからやり直してください。",
      );
      return;
    }
    // 操作時刻と冪等キーは GPS 取得待ちより前に固定し、初回送信と再送で同じ値を使う。
    const idempotencyKey = generatePublicId();
    const occurredAt = new Date().toISOString();
    // F16/F20: GPS は best-effort。失敗時は座標なしで打刻を続行し警告を出す。
    const pos = await captureCurrentPosition();
    setGeoWarning(pos ? null : "位置情報を取得できませんでした(打刻は記録されます)");
    const body: ClockInInput & { idempotency_key: string; occurred_at: string } = {
      idempotency_key: idempotencyKey,
      occurred_at: occurredAt,
      weather,
      ...(odometer_start !== undefined ? { odometer_start } : {}),
      ...(pos ? { latitude: pos.latitude, longitude: pos.longitude } : {}),
      alcohol_result: alcohol.result,
      ...(alcohol.measured_value !== undefined
        ? { alcohol_measured_value: alcohol.measured_value }
        : {}),
    };
    await runAction(async () => {
      try {
        await clockInDriver(fetcher, body);
      } catch (e) {
        enqueueRetryable(e, {
          kind: "clock-in",
          path: "/api/v1/driver/attendance/clock-in",
          method: "POST",
          body,
        });
        throw e;
      }
      await attendance.refetch();
    });
  }

  async function performClockOut(odometer_end?: number) {
    // 操作時刻と冪等キーは GPS 取得待ちより前に固定し、初回送信と再送で同じ値を使う。
    const idempotencyKey = generatePublicId();
    const occurredAt = new Date().toISOString();
    // F16/F20: GPS は best-effort。失敗時は座標なしで打刻を続行し警告を出す。
    const pos = await captureCurrentPosition();
    setGeoWarning(pos ? null : "位置情報を取得できませんでした(打刻は記録されます)");
    const body: ClockOutInput & { idempotency_key: string; occurred_at: string } = {
      idempotency_key: idempotencyKey,
      occurred_at: occurredAt,
      ...(odometer_end !== undefined ? { odometer_end } : {}),
      ...(pos ? { latitude: pos.latitude, longitude: pos.longitude } : {}),
    };
    await runAction(async () => {
      try {
        await clockOutDriver(fetcher, body);
      } catch (e) {
        enqueueRetryable(e, {
          kind: "clock-out",
          path: "/api/v1/driver/attendance/clock-out",
          method: "POST",
          body,
        });
        throw e;
      }
      await attendance.refetch();
    });
  }

  function handleClockIn() {
    setClockInDialog({ open: true });
  }

  /**
   * #628: 退勤操作の入口。到着オドメーター未入力があれば先に一覧で提示する。
   * 未入力が 0 件なら従来どおり退勤打刻の確認をそのまま開く (操作数を増やさない)。
   * 配送実行が止まっている間 (アルコール / ルート編集中) は、その場で入力できないので提示しない。
   */
  function openClockOutFlow() {
    if (missingOdometerStops.length > 0) {
      setMissingOdoDialog({ open: true });
      return;
    }
    setClockOutDialog({ open: true });
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] font-[family-name:var(--f-sans)] text-[var(--ink)]">
      {state.status === "loading" && (
        <div className="p-6 text-center text-[length:var(--fs-sm)] text-[var(--ink-3)]">
          読み込み中…
        </div>
      )}
      {state.status === "error" && (
        <div
          role="alert"
          className="m-4 rounded-lg bg-[var(--st-cancel-bg)] px-3.5 py-3 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]"
        >
          プロファイル取得エラー: {state.message}
        </div>
      )}

      {state.status === "ready" && screen.kind === "attendance" && (
        <>
          <Header
            title="勤怠打刻"
            sub="現在の勤怠状態を確認しています"
            showBack
            onBack={() => {
              setSelectedDate(undefined);
              setClockInDialog({ open: false });
              setClockOutDialog({ open: false });
              setScreen({ kind: "list" });
            }}
          />
          {/* #548: リッチメニューから来ても、出勤ゲートが閉じているならダイアログを開かず理由を出す。
              退勤 (出勤済み) はゲートの対象外なので、未出勤のときだけ理由を表示する。 */}
          {attendance.loaded && !attendance.attendance?.clock_in_at && clockInBlockedReason ? (
            <div className="p-6 text-center text-[length:var(--fs-sm)]">
              <p role="alert" className="font-semibold text-[var(--st-cancel-ink)]">
                {clockInBlockedReason}
              </p>
              <p className="mt-2 text-[var(--ink-3)]">
                ルートが確定すると出勤できます。配車担当へ連絡してください。
              </p>
            </div>
          ) : (
            <div
              aria-live="polite"
              className="p-6 text-center text-[length:var(--fs-sm)] text-[var(--ink-3)]"
            >
              {attendance.loaded ? "打刻内容を確認してください。" : "勤怠状態を確認中…"}
            </div>
          )}
        </>
      )}

      {state.status === "ready" && screen.kind === "list" && (
        <>
          <Header
            title={`${displayName} さん`}
            // 便区分は日付ストリップとタブで判別できるため、ヘッダーは日付のみにする。
            // ルート 0 件の日でも選択日を出すため、応答ではなく選択日を表示源にする (#768)。
            sub={formatDisplayDate(viewDate)}
            tabs={["今日", "明日", "履歴"]}
            active={activeTab}
            onTab={(t) => {
              if (t === "履歴") {
                setScreen({ kind: "history" });
                return;
              }
              showRouteDate(t === "今日" ? jstDateOffset(0) : jstDateOffset(1));
            }}
          />
          {/* #768: 過去日 (最大 PAST_ROUTE_DAYS 日前) 〜 翌日を切り替える。今日以外は閲覧のみ。 */}
          <DateStrip
            value={viewDate}
            min={dateRange.min}
            max={dateRange.max}
            onChange={showRouteDate}
          />
          <ApiStatusBanner
            session={session}
            schedule={schedule}
            attendance={attendance}
            acting={acting}
            actionError={actionError}
            geoWarning={geoWarning}
            pendingQueue={pendingQueue}
            deadLetters={deadLetters}
            deliveryExecutionAllowed={deliveryExecutionAllowed}
            routeEditing={routeEditing}
            // 今日以外 (過去日・翌日) は閲覧のみ。打刻導線は出さない。
            showAttendanceActions={isTodayView}
            clockInBlockedReason={clockInBlockedReason}
            onClockIn={() => void handleClockIn()}
            onClockOut={() => openClockOutFlow()}
            onFlush={() => void flushQueue()}
            onRetryDeadLetter={handleRetryDeadLetter}
            onDiscardDeadLetter={handleDiscardDeadLetter}
          />
          {isTodayView && (
            <SummaryStrip
              stops={routeView.stops}
              onOpenDailyReport={() => setScreen({ kind: "daily-report" })}
              // 日報は出勤した日のみ作成可。clock_in_at は退勤後も残るため退勤後も作成できる。
              canCreateReport={!!attendance.attendance?.clock_in_at}
            />
          )}
          {/* 今日以外は閲覧のみ。車両点検・給油・経費の作業導線は今日だけに出す。 */}
          {isTodayView && (
            <QuickActions
              onInspection={() => setScreen({ kind: "inspection" })}
              onExpense={() => setScreen({ kind: "expense" })}
              onExpensesList={() => setScreen({ kind: "expenses-list" })}
              onRefueling={() => setScreen({ kind: "refueling" })}
              inspectionDisabledReason={inspectionGateReason}
              refuelingDisabledReason={workGateReason}
              expenseDisabledReason={workGateReason}
            />
          )}
          <StopGroups
            stops={routeView.stops}
            onSelect={(s) => setScreen({ kind: "detail", stopId: s.id })}
            openExternalUrl={openExternalUrl}
            acting={acting}
            // 今日以外は閲覧のみ。今日のみ一覧から直接打刻できる。
            emptyMessage={
              isTodayView ? undefined : `${formatRouteDate(viewDate)} のルートはありません`
            }
            onDepart={
              !isTodayView || !deliveryExecutionAllowed ? undefined : (s) => requestDepart(s.id)
            }
            onArrive={
              !isTodayView || !deliveryExecutionAllowed
                ? undefined
                : (s) => {
                    // 配達先は到着時オドメーター入力ダイアログを挟む (詳細画面と同じ導線)。
                    if (s.kind === "delivery") {
                      setArriveOdoDialog({ open: true, stopId: s.id, mode: "arrive" });
                      return;
                    }
                    void arriveStop(s.id);
                  }
            }
            onComplete={
              !isTodayView || !deliveryExecutionAllowed ? undefined : (s) => void completeStop(s.id)
            }
            onDelay={
              !isTodayView || !deliveryExecutionAllowed
                ? undefined
                : (s) => setScreen({ kind: "delay", stopId: s.id, returnTo: "list" })
            }
          />
          <Footer
            apiBaseUrl={apiBaseUrl}
            pendingQueue={pendingQueue}
            flushedAt={flushedAt}
            onFlush={() => void flushQueue()}
          />
        </>
      )}

      {state.status === "ready" && screen.kind === "detail" && selectedStop && (
        <StopDetail
          stop={selectedStop}
          acting={acting}
          error={actionError}
          fetcher={fetcher}
          // #768: 詳細には日付ストリップが無いので、今日以外は見出しに表示日を出す。
          dateLabel={isTodayView ? undefined : formatRouteDate(viewDate)}
          // 今日以外 (過去日・翌日) は閲覧のみ。打刻/取消/遅延/オドメーター入力は不可。
          readOnly={!isTodayView || !deliveryExecutionAllowed}
          departDisabledReason={departureBlockedReason(routeView.stops, selectedStop.id)}
          openExternalUrl={openExternalUrl}
          onBack={() => setScreen({ kind: "list" })}
          onView={() => confirmRouteInstruction(selectedStop.routeId)}
          onDepart={async () => {
            // 出発打刻後は詳細画面に留まる。schedule.refetch() で apiStatus が in_transit に
            // 更新され「到着打刻」ボタンに自動切替わる。点検未実施時は警告を挟む (#401)。
            requestDepart(selectedStop.id);
          }}
          onAdvance={async () => {
            // 到着打刻後は詳細画面に留まる。schedule.refetch() で apiStatus が arrived に更新され
            // 「引渡し完了」ボタンに自動切替わる。
            // F29 委託先支払距離: 配達先は到着時オドメーター入力ダイアログを挟む (集荷等はそのまま打刻)。
            if (selectedStop.kind === "delivery") {
              setArriveOdoDialog({ open: true, stopId: selectedStop.id, mode: "arrive" });
              return;
            }
            await arriveStop(selectedStop.id);
          }}
          onEditOdometer={
            !isTodayView || !deliveryExecutionAllowed
              ? undefined
              : () => setArriveOdoDialog({ open: true, stopId: selectedStop.id, mode: "edit" })
          }
          onComplete={async () => {
            await completeStop(selectedStop.id);
            setScreen({ kind: "list" });
          }}
          onCancel={async (reason) => {
            await cancelStop(selectedStop.id, reason);
            setScreen({ kind: "list" });
          }}
          onDelay={() => setScreen({ kind: "delay", stopId: selectedStop.id, returnTo: "detail" })}
          onShowDocument={() => setScreen({ kind: "order-document", stopId: selectedStop.id })}
          // #616: 配達先に固定して経費フォームを開く。閲覧専用 (今日以外) と出勤前は出さない
          // (経費は出勤打刻がある日にしか登録できないため)。集荷 stop は注文単位で配達先に寄せる。
          onAddExpense={
            !isTodayView || workGateReason || selectedStop.kind !== "delivery"
              ? undefined
              : () => {
                  setEditingExpense(null);
                  setScreen({ kind: "expense", stopId: selectedStop.id });
                }
          }
          onRevert={
            deliveryExecutionAllowed
              ? () => setScreen({ kind: "revert", stopId: selectedStop.id })
              : undefined
          }
          onEditTimestamp={
            deliveryExecutionAllowed
              ? () => setScreen({ kind: "timestamp-edit", stopId: selectedStop.id })
              : undefined
          }
          onShowProof={
            deliveryExecutionAllowed
              ? () => setScreen({ kind: "proof", stopId: selectedStop.id })
              : undefined
          }
          // #839: 過去日の未完了 stop は、この画面からその場で実績を後追い登録できる。
          // 日報や勤怠の有無には依存しない (createDriverBackfillRequest が保存時点で即時反映する)。
          backfill={
            !isTodayView &&
            selectedStop.apiStatus !== "completed" &&
            selectedStop.apiStatus !== "skipped" &&
            selectedStop.apiStatus !== "cancelled"
              ? {
                  workDate: viewDate,
                  fetcher,
                  onSaved: () => scheduleRefetch(),
                }
              : undefined
          }
        />
      )}

      {state.status === "ready" && screen.kind === "delay" && selectedStop && (
        <DelayReport
          stop={selectedStop}
          acting={acting}
          error={actionError}
          onBack={() =>
            setScreen(
              screen.returnTo === "list"
                ? { kind: "list" }
                : { kind: "detail", stopId: selectedStop.id },
            )
          }
          onSubmit={async (input) => {
            if (!deliveryExecutionAllowed) return;
            await reportDelay(selectedStop.id, input);
            setScreen(
              screen.returnTo === "list"
                ? { kind: "list" }
                : { kind: "detail", stopId: selectedStop.id },
            );
          }}
        />
      )}

      {state.status === "ready" && screen.kind === "revert" && selectedStop && (
        <StatusRevertConfirm
          stop={selectedStop}
          acting={acting}
          error={actionError}
          onBack={() => setScreen({ kind: "detail", stopId: selectedStop.id })}
          onSubmit={async (reason) => {
            if (!deliveryExecutionAllowed) return;
            await revertStop(selectedStop.id, reason);
            setScreen({ kind: "detail", stopId: selectedStop.id });
          }}
        />
      )}

      {state.status === "ready" && screen.kind === "timestamp-edit" && selectedStop && (
        <TimestampEdit
          stop={selectedStop}
          isPickup={
            selectedStop.kind === "pickup" ||
            selectedStop.kind === "collect" ||
            selectedStop.kind === "waypoint"
          }
          acting={acting}
          error={actionError}
          onBack={() => setScreen({ kind: "detail", stopId: selectedStop.id })}
          onSubmit={async (input) => {
            if (!deliveryExecutionAllowed) return;
            await editTimestamps(selectedStop.id, input);
            setScreen({ kind: "detail", stopId: selectedStop.id });
          }}
        />
      )}

      {state.status === "ready" &&
        screen.kind === "proof" &&
        selectedStop &&
        (deliveryExecutionAllowed ? (
          <ProofUpload
            stop={selectedStop}
            fetcher={fetcher}
            onBack={() => setScreen({ kind: "detail", stopId: selectedStop.id })}
          />
        ) : (
          <div role="alert" className="m-4 rounded-lg bg-[var(--st-cancel-bg)] p-4">
            {proofBlockedMessage(executionBlockedReason ?? "alcohol")}
          </div>
        ))}

      {state.status === "ready" && screen.kind === "daily-report" && (
        <DailyReportScreen
          key={driverRecordRevision}
          session={session}
          fetcher={fetcher}
          routeView={routeView}
          attendance={attendance.attendance}
          inspectionDone={inspectionDone}
          alcoholDone={alcoholDone}
          onBack={() => setScreen({ kind: "list" })}
          onGoInspection={() => setScreen({ kind: "inspection" })}
          onGoAlcohol={() => setScreen({ kind: "alcohol" })}
          onGoExpense={() => setScreen({ kind: "expense" })}
        />
      )}

      {state.status === "ready" && screen.kind === "inspection" && (
        <InspectionScreen
          fetcher={fetcher}
          assignedVehiclePublicIds={assignedVehiclePublicIds}
          onBack={() => setScreen({ kind: "list" })}
          onDone={() => setInspectionDone(true)}
        />
      )}

      {state.status === "ready" && screen.kind === "alcohol" && (
        <AlcoholScreen
          fetcher={fetcher}
          onBack={() => setScreen({ kind: "list" })}
          onDone={() => {
            setAlcoholDone(true);
            void attendance.refetch();
          }}
        />
      )}

      {state.status === "ready" && screen.kind === "expense" && (
        <ExpenseFormScreen
          fetcher={fetcher}
          editExpense={editingExpense ?? undefined}
          lockedStop={expenseLockedStop}
          onBack={() => {
            // 配送先詳細から開いた場合はその詳細へ戻す (呼び出し元に返すのが自然)。
            const stopId = screen.kind === "expense" ? screen.stopId : undefined;
            setScreen(
              stopId
                ? { kind: "detail", stopId }
                : { kind: editingExpense ? "expenses-list" : "list" },
            );
            setEditingExpense(null);
          }}
          onEnqueue={() => setPendingQueue(queueSize())}
          onSaved={async () => {
            await refreshDriverRecordQueries();
            setExpenseMutationNotice("経費を変更しました");
            setEditingExpense(null);
            setScreen({ kind: "expenses-list" });
          }}
        />
      )}

      {state.status === "ready" && screen.kind === "expenses-list" && (
        <ExpensesListScreen
          fetcher={fetcher}
          successMessage={expenseMutationNotice}
          onBack={() => {
            setExpenseMutationNotice(null);
            setScreen({ kind: "list" });
          }}
          onAdd={() => {
            setExpenseMutationNotice(null);
            setEditingExpense(null);
            setScreen({ kind: "expense" });
          }}
          onEdit={(expense) => {
            setExpenseMutationNotice(null);
            setEditingExpense(expense);
            setScreen({ kind: "expense" });
          }}
          onChanged={refreshDriverRecordQueries}
        />
      )}

      {state.status === "ready" && screen.kind === "refueling" && (
        <RefuelingScreen
          fetcher={fetcher}
          onBack={() => setScreen({ kind: "list" })}
          onChanged={refreshDriverRecordQueries}
        />
      )}

      {state.status === "ready" && screen.kind === "order-document" && (
        <OrderDocumentScreen
          fetcher={fetcher}
          stopPublicId={screen.stopId}
          onBack={() => setScreen({ kind: "detail", stopId: screen.stopId })}
        />
      )}

      {state.status === "ready" && screen.kind === "history" && (
        <HistoryScreen
          fetcher={fetcher}
          initialReportPublicId={screen.reportId}
          initialWorkDate={screen.workDate}
          onBack={() => {
            setSelectedDate(undefined);
            setScreen({ kind: "list" });
          }}
        />
      )}

      {/* #401: 点検未実施のまま出発しようとしたときの警告。続行は可能 (最終担保は退勤ゲート)。 */}
      {departInspectionWarn.open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="depart-inspection-warn-title"
          aria-describedby="depart-inspection-warn-description"
          className="safe-bottom fixed inset-0 z-50 flex items-end bg-black/40 px-3"
        >
          <div className="w-full rounded-lg bg-[var(--bg-raised)] p-4 shadow-lg">
            <h2
              id="depart-inspection-warn-title"
              className="mb-2 text-[length:var(--fs-md)] font-semibold text-[var(--ink-1)]"
            >
              車両点検が未実施です
            </h2>
            <p
              id="depart-inspection-warn-description"
              className="mb-4 text-[length:var(--fs-sm)] text-[var(--ink-2)]"
            >
              出発前に車両点検を実施してください。点検が未実施のままだと退勤打刻ができません。
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  const stopId = departInspectionWarn.stopId;
                  setDepartInspectionWarn({ open: false, stopId: null });
                  if (stopId) void departStop(stopId);
                }}
                className="cursor-pointer rounded-lg border border-[var(--rule-soft)] bg-transparent py-2.5 text-[length:var(--fs-sm)] font-medium text-[var(--ink-2)]"
              >
                このまま出発
              </button>
              <button
                type="button"
                onClick={() => {
                  setDepartInspectionWarn({ open: false, stopId: null });
                  setScreen({ kind: "inspection" });
                }}
                className="cursor-pointer rounded-lg bg-[var(--accent)] py-2.5 text-[length:var(--fs-sm)] font-semibold text-white"
              >
                点検する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* #628: 退勤操作時の到着オドメーター未入力の提示。退勤はブロックせず「あとで」で続行できる。 */}
      {missingOdoDialog.open && (
        <MissingOdometerDialog
          stops={missingOdometerStops}
          acting={acting}
          error={actionError}
          onEnter={(stop) => setArriveOdoDialog({ open: true, stopId: stop.id, mode: "edit" })}
          onProceed={() => {
            setMissingOdoDialog({ open: false });
            setClockOutDialog({ open: true });
          }}
          onCancel={() => {
            setMissingOdoDialog({ open: false });
            if (screen.kind === "attendance") {
              setSelectedDate(undefined);
              setScreen({ kind: "list" });
            }
          }}
        />
      )}
      {clockInDialog.open && (
        <OdometerDialog
          title="出勤打刻"
          description="天気、出勤時 オドメーター (km)、アルコール検査結果を入力してください。オドメーターは空欄でも打刻できます。"
          confirmLabel="出勤打刻する"
          withAlcohol
          withWeather
          onCancel={() => {
            setClockInDialog({ open: false });
            if (screen.kind === "attendance") {
              setSelectedDate(undefined);
              setScreen({ kind: "list" });
            }
          }}
          onConfirm={async (n, alcohol, weather) => {
            if (!weather) return;
            if (screen.kind === "attendance") {
              setSelectedDate(undefined);
              setScreen({ kind: "list" });
            }
            // API 完了まで待ってから閉じる。ダイアログを開いたままにすることで
            // OdometerDialog 側の「送信中…」表示が通信中も見える。
            // withAlcohol=true のため alcohol は常に渡される。
            await performClockIn(n, alcohol ?? { result: "pass" }, weather);
            setClockInDialog({ open: false });
          }}
        />
      )}
      {clockOutDialog.open && (
        <OdometerDialog
          title="退勤打刻"
          description="退勤時 オドメーター (km) を入力してください。打刻すると本日の運転日報が自動生成されます。"
          confirmLabel="退勤打刻する"
          destructive
          onCancel={() => {
            setClockOutDialog({ open: false });
            if (screen.kind === "attendance") {
              setSelectedDate(undefined);
              setScreen({ kind: "list" });
            }
          }}
          onConfirm={async (n) => {
            if (screen.kind === "attendance") {
              setSelectedDate(undefined);
              setScreen({ kind: "list" });
            }
            // API 完了まで待ってから閉じる (送信中表示のため)。
            await performClockOut(n);
            setClockOutDialog({ open: false });
          }}
        />
      )}
      {arriveOdoDialog.open && arriveOdoDialog.stopId && (
        <OdometerDialog
          title={arriveOdoDialog.mode === "edit" ? "オドメーター入力 / 修正" : "到着打刻"}
          description={
            arriveOdoDialog.mode === "edit"
              ? "到着時 オドメーター (km) を入力・修正してください。委託先支払距離の算出に使います。空欄で確定すると登録を消去します。"
              : "到着時 オドメーター (km) を入力してください。委託先支払距離の算出に使います。オドメーターは空欄でも打刻できます。"
          }
          // 退勤時の未入力一覧 (#628) からも開くため、詳細画面の selectedStop ではなく
          // ダイアログが持つ stopId で当日ルートから引く。
          defaultValue={
            arriveOdoDialog.mode === "edit"
              ? (routeView.stops.find((s) => s.id === arriveOdoDialog.stopId)?.arrivalOdometer ??
                undefined)
              : undefined
          }
          confirmLabel={arriveOdoDialog.mode === "edit" ? "保存する" : "到着打刻する"}
          onCancel={() => setArriveOdoDialog({ open: false, stopId: null, mode: "arrive" })}
          onConfirm={async (n) => {
            const { stopId, mode } = arriveOdoDialog;
            // API 完了まで待ってから閉じる (送信中表示のため)。
            if (stopId) {
              if (mode === "edit") await editStopOdometer(stopId, n);
              else await arriveStop(stopId, n);
            }
            setArriveOdoDialog({ open: false, stopId: null, mode: "arrive" });
          }}
        />
      )}
    </div>
  );
}
